import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { 
  Users, 
  Plus, 
  Trash2, 
  Edit, 
  Loader2,
  Phone,
  Briefcase,
  DollarSign,
  FileText,
  Printer,
  Banknote,
  TrendingUp,
  TrendingDown,
  Wallet,
  UserMinus,
  Calendar
} from 'lucide-react';

const TRANSACTION_TYPES = {
  salary: { label: 'راتب', color: 'bg-green-100 text-green-700', icon: '💰' },
  advance: { label: 'سلفة', color: 'bg-amber-100 text-amber-700', icon: '💸' },
  deduction: { label: 'خصم', color: 'bg-red-100 text-red-700', icon: '➖' },
  bonus: { label: 'مكافأة', color: 'bg-blue-100 text-blue-700', icon: '🎁' }
};

const EmployeesPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';

  const [activeTab, setActiveTab] = useState('employees');
  const [loading, setLoading] = useState(true);
  
  // Employees
  const [employees, setEmployees] = useState([]);
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deleteEmployeeDialog, setDeleteEmployeeDialog] = useState(false);
  
  // Transactions
  const [transactionDialog, setTransactionDialog] = useState(false);
  const [statementDialog, setStatementDialog] = useState(false);
  const [statement, setStatement] = useState(null);
  const [statementDates, setStatementDates] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  
  const [formLoading, setFormLoading] = useState(false);

  const fc = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees?active_only=false`, { headers: getAuthHeader() });
      setEmployees(res.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeader]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Employee handlers
  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      phone: formData.get('phone') || '',
      position: formData.get('position') || '',
      salary: parseFloat(formData.get('salary')) || 0,
      start_date: formData.get('start_date') || '',
      notes: formData.get('notes') || ''
    };

    try {
      if (selectedEmployee) {
        await axios.put(`${API_URL}/api/employees/${selectedEmployee.id}`, data, { headers: getAuthHeader() });
        toast.success('تم تحديث بيانات الموظف');
      } else {
        await axios.post(`${API_URL}/api/employees`, data, { headers: getAuthHeader() });
        toast.success('تم إضافة الموظف');
      }
      setEmployeeDialog(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteEmployee = async () => {
    try {
      await axios.delete(`${API_URL}/api/employees/${selectedEmployee.id}`, { headers: getAuthHeader() });
      toast.success('تم حذف الموظف');
      setDeleteEmployeeDialog(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error('حدث خطأ في الحذف');
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const formData = new FormData(e.target);
    const data = {
      employee_id: selectedEmployee.id,
      transaction_type: formData.get('transaction_type'),
      amount: parseFloat(formData.get('amount')),
      date: formData.get('date'),
      notes: formData.get('notes') || ''
    };

    try {
      await axios.post(`${API_URL}/api/employees/${selectedEmployee.id}/transactions`, data, { headers: getAuthHeader() });
      toast.success('تم تسجيل العملية');
      setTransactionDialog(false);
      fetchStatement();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setFormLoading(false);
    }
  };

  const fetchStatement = async () => {
    if (!selectedEmployee) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/employees/${selectedEmployee.id}/statement?start_date=${statementDates.start}&end_date=${statementDates.end}`,
        { headers: getAuthHeader() }
      );
      setStatement(res.data);
    } catch (error) {
      toast.error('خطأ في تحميل كشف الحساب');
    }
  };

  const openStatement = (emp) => {
    setSelectedEmployee(emp);
    setStatementDialog(true);
  };

  useEffect(() => {
    if (statementDialog && selectedEmployee) {
      fetchStatement();
    }
  }, [statementDialog, selectedEmployee, statementDates]);

  const handlePrintStatement = () => {
    if (!statement) return;

    const rows = statement.transactions.map(t => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${t.date}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">
          <span style="padding:2px 8px;border-radius:4px;font-size:12px" class="${t.transaction_type}">
            ${TRANSACTION_TYPES[t.transaction_type]?.icon} ${TRANSACTION_TYPES[t.transaction_type]?.label}
          </span>
        </td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:bold;color:${['salary', 'bonus'].includes(t.transaction_type) ? '#059669' : '#dc2626'}">
          ${['salary', 'bonus'].includes(t.transaction_type) ? '+' : '-'}${fc(t.amount)} €
        </td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${t.notes || '-'}</td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>كشف حساب موظف - ${statement.employee.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; direction: rtl; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #1e40af; color: white; padding: 10px; text-align: right; }
          .summary { display: flex; gap: 15px; margin-top: 20px; flex-wrap: wrap; }
          .summary-box { padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; }
          .salary { background: #d1fae5; }
          .advance { background: #fef3c7; }
          .deduction { background: #fee2e2; }
          .bonus { background: #dbeafe; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1 style="color:#1e40af">كشف حساب موظف</h1>
        <h2>${statement.employee.name}</h2>
        <p>المنصب: ${statement.employee.position || '-'} | الراتب: ${fc(statement.employee.salary)} €</p>
        <p>الفترة: ${statement.start_date} — ${statement.end_date}</p>

        <div class="summary">
          <div class="summary-box salary">
            <div style="font-size:12px">الرواتب</div>
            <div style="font-size:20px;font-weight:bold">${fc(statement.summary.total_salary)} €</div>
          </div>
          <div class="summary-box bonus">
            <div style="font-size:12px">المكافآت</div>
            <div style="font-size:20px;font-weight:bold">${fc(statement.summary.total_bonus)} €</div>
          </div>
          <div class="summary-box advance">
            <div style="font-size:12px">السلف</div>
            <div style="font-size:20px;font-weight:bold">${fc(statement.summary.total_advance)} €</div>
          </div>
          <div class="summary-box deduction">
            <div style="font-size:12px">الخصومات</div>
            <div style="font-size:20px;font-weight:bold">${fc(statement.summary.total_deduction)} €</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>النوع</th>
              <th>المبلغ</th>
              <th>ملاحظات</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>

        <div style="margin-top:30px;padding:15px;background:${statement.summary.balance >= 0 ? '#d1fae5' : '#fee2e2'};border-radius:8px;text-align:center">
          <span style="font-size:14px">الرصيد النهائي:</span>
          <span style="font-size:24px;font-weight:bold;margin-right:10px">${fc(statement.summary.balance)} €</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="employees-page" className="space-y-5" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-bold text-stone-900 flex items-center gap-2">
          <Users className="w-6 h-6 text-blue-600" />
          الموظفين
        </h1>
        <Button onClick={() => { setSelectedEmployee(null); setEmployeeDialog(true); }} className="gap-1">
          <Plus className="w-4 h-4" /> إضافة موظف
        </Button>
      </div>

      {/* Employees Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-8 text-center text-stone-500">
              لا يوجد موظفين. أضف موظف جديد للبدء.
            </CardContent>
          </Card>
        ) : (
          employees.map((emp) => (
            <Card key={emp.id} className={`hover:shadow-md transition-shadow ${!emp.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    {emp.name}
                    {!emp.is_active && <span className="text-xs text-red-500">(غير نشط)</span>}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openStatement(emp)} title="كشف الحساب">
                      <FileText className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedEmployee(emp); setTransactionDialog(true); }} title="إضافة عملية">
                      <Banknote className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedEmployee(emp); setEmployeeDialog(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setSelectedEmployee(emp); setDeleteEmployeeDialog(true); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {emp.position && (
                  <p className="flex items-center gap-2 text-stone-600">
                    <Briefcase className="w-4 h-4" /> {emp.position}
                  </p>
                )}
                {emp.phone && (
                  <p className="flex items-center gap-2 text-stone-500">
                    <Phone className="w-4 h-4" /> {emp.phone}
                  </p>
                )}
                <div className="pt-2 border-t flex justify-between">
                  <span className="text-stone-600">الراتب:</span>
                  <span className="font-bold text-blue-600">{fc(emp.salary)} €</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Employee Dialog */}
      <Dialog open={employeeDialog} onOpenChange={setEmployeeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedEmployee ? 'تعديل موظف' : 'إضافة موظف جديد'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEmployee} className="space-y-4">
            <div>
              <Label>اسم الموظف *</Label>
              <Input name="name" defaultValue={selectedEmployee?.name} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>المنصب</Label>
                <Input name="position" defaultValue={selectedEmployee?.position} placeholder="سائق، عامل..." />
              </div>
              <div>
                <Label>الهاتف</Label>
                <Input name="phone" defaultValue={selectedEmployee?.phone} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>الراتب (€)</Label>
                <Input name="salary" type="number" step="0.01" defaultValue={selectedEmployee?.salary || 0} />
              </div>
              <div>
                <Label>تاريخ البدء</Label>
                <Input name="start_date" type="date" defaultValue={selectedEmployee?.start_date} />
              </div>
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input name="notes" defaultValue={selectedEmployee?.notes} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEmployeeDialog(false)}>إلغاء</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transaction Dialog */}
      <Dialog open={transactionDialog} onOpenChange={setTransactionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>تسجيل عملية - {selectedEmployee?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddTransaction} className="space-y-4">
            <div>
              <Label>نوع العملية *</Label>
              <Select name="transaction_type" defaultValue="salary" required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salary">💰 راتب</SelectItem>
                  <SelectItem value="advance">💸 سلفة</SelectItem>
                  <SelectItem value="deduction">➖ خصم</SelectItem>
                  <SelectItem value="bonus">🎁 مكافأة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>المبلغ (€) *</Label>
                <Input name="amount" type="number" step="0.01" required />
              </div>
              <div>
                <Label>التاريخ *</Label>
                <Input name="date" type="date" defaultValue={format(new Date(), 'yyyy-MM-dd')} required />
              </div>
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input name="notes" placeholder="مثال: راتب شهر سبتمبر" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransactionDialog(false)}>إلغاء</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'تسجيل'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Statement Dialog */}
      <Dialog open={statementDialog} onOpenChange={setStatementDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>كشف حساب - {selectedEmployee?.name}</span>
              <Button variant="outline" size="sm" onClick={handlePrintStatement} className="gap-1">
                <Printer className="w-4 h-4" /> طباعة
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {/* Date Filter */}
          <div className="flex gap-3 items-end mb-4">
            <div>
              <Label>من</Label>
              <Input
                type="date"
                value={statementDates.start}
                onChange={(e) => setStatementDates({ ...statementDates, start: e.target.value })}
                className="w-36"
              />
            </div>
            <div>
              <Label>إلى</Label>
              <Input
                type="date"
                value={statementDates.end}
                onChange={(e) => setStatementDates({ ...statementDates, end: e.target.value })}
                className="w-36"
              />
            </div>
            <Button onClick={fetchStatement} size="sm">تحديث</Button>
          </div>

          {statement && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600">الرواتب</p>
                  <p className="text-lg font-bold text-green-700">{fc(statement.summary.total_salary)} €</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600">المكافآت</p>
                  <p className="text-lg font-bold text-blue-700">{fc(statement.summary.total_bonus)} €</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-600">السلف</p>
                  <p className="text-lg font-bold text-amber-700">{fc(statement.summary.total_advance)} €</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-red-600">الخصومات</p>
                  <p className="text-lg font-bold text-red-700">{fc(statement.summary.total_deduction)} €</p>
                </div>
                <div className={`border-2 rounded-lg p-3 text-center ${statement.summary.balance >= 0 ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'}`}>
                  <p className="text-xs text-stone-600">الرصيد</p>
                  <p className={`text-lg font-bold ${statement.summary.balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {fc(statement.summary.balance)} €
                  </p>
                </div>
              </div>

              {/* Transactions Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>التاريخ</TableHead>
                    <TableHead>النوع</TableHead>
                    <TableHead className="text-center">المبلغ</TableHead>
                    <TableHead>ملاحظات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-stone-500">
                        لا توجد عمليات في هذه الفترة
                      </TableCell>
                    </TableRow>
                  ) : (
                    statement.transactions.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>{t.date}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${TRANSACTION_TYPES[t.transaction_type]?.color}`}>
                            {TRANSACTION_TYPES[t.transaction_type]?.icon} {TRANSACTION_TYPES[t.transaction_type]?.label}
                          </span>
                        </TableCell>
                        <TableCell className={`text-center font-bold ${['salary', 'bonus'].includes(t.transaction_type) ? 'text-green-600' : 'text-red-600'}`}>
                          {['salary', 'bonus'].includes(t.transaction_type) ? '+' : '-'}{fc(t.amount)} €
                        </TableCell>
                        <TableCell>{t.notes || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteEmployeeDialog} onOpenChange={setDeleteEmployeeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف هذا الموظف؟ سيتم حذف جميع العمليات المرتبطة به.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEmployee} className="bg-red-600 hover:bg-red-700">
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmployeesPage;
